import { useTranslation } from "react-i18next"

import type { EntityRef } from "types/common.types"
import type { DeviceSettings } from "types/device.types"

import usePowerline from "helpers/hooks/powerlines/usePowerline"
import useSpan from "helpers/hooks/spans/useSpan"
import useTower from "helpers/hooks/towers/useTower"
import { getSpanDisplayName } from "helpers/utils/spans"

export type InstalledOnRef = EntityRef & { entity: "span" | "tower" }

interface UseInstalledOnRefReturn {
  ref: InstalledOnRef | undefined
  isLoading: boolean
}

/** The span or tower a device is clamped to, with the name its link shows. */
const useInstalledOnRef = (
  settings: DeviceSettings | undefined,
): UseInstalledOnRefReturn => {
  const { t } = useTranslation()

  const spanId =
    settings?.installation_type === "powerline" ? settings.span_id : undefined
  const towerId = settings?.installation_type === "tower" ? settings.tower_id : undefined
  const { span, isLoading: spanLoading } = useSpan({ id: spanId })
  // The line supplies the position a span without a name falls back to.
  const { powerline, isLoading: powerlineLoading } = usePowerline({
    id: spanId ? settings?.powerline_id : undefined,
  })
  const { tower, isLoading: towerLoading } = useTower({ id: towerId })

  const ref: InstalledOnRef | undefined = spanId
    ? {
        entity: "span",
        id: spanId,
        name: getSpanDisplayName(
          { id: spanId, name: span?.name ?? null },
          powerline?.span_ids,
          t,
        ),
      }
    : tower
      ? { entity: "tower", id: tower.id, name: tower.name }
      : undefined

  // The span's name replaces its position once the span lands, so wait for it
  // rather than show one and swap it.
  return { ref, isLoading: spanLoading || powerlineLoading || towerLoading }
}

export default useInstalledOnRef
